import { useSession } from "next-auth/react";
import React, { Fragment, useEffect, useRef, useState } from "react";
import eventClientApi from "@/api-client/event-client";
import Avatar from "@/components/ui/avatar";
import { Loader } from "@/components/ui/loader";
import { Text } from "@/components/ui/text";
import { Add, Edit, SearchNormal1 } from "iconsax-react";

import { usePaticipantsInvite } from "@/stores/use-guest-invite-store";
import Multiline from "@/components/ui/multiline";
// import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs-variant1.";
import NoDataView from "@/components/ui/no-data-view";
import { Check, ChevronRight, X } from "lucide-react";
import { motion } from "framer-motion";
import CreateGroup from "./create";
import AddMember from "./add-member";
import GroupGuests from "./group-users";
import { useGuestlistGroup } from "@/hooks/use-guestlist-group";
import useInView from "@/hooks/use-in-view";
import { useQueryClient } from "@tanstack/react-query";
import { ResourceState } from "@/modals/invite-guest-modal";
import { useGuestlist } from "@/hooks/use-event-group-participants";
import { getQueryKey } from "@key.ai/constants";
import { Input } from "@/components/ui/input";
import useDebounce from "@/hooks/use-debounce";
import SkeletonListLoader from "@/components/app/manage-event/guest-skelaton";

const tabs = [
  {
    key: "email",
    title: "Email"
  }
  // {
  //   key: "pastEvents",
  //   title: "Past Events"
  // }
];

interface GroupProps {
  toggleUserSelection: (
    type: keyof ResourceState,
    resourceId: string,
    user: string,
    select: boolean
  ) => void;
}

const Group = ({
  emails,
  setEmails,
  role,
  resourceState,
  setResourceState,
  toggleUserSelection,
  checkIsUserPresent,
  toggleSelectAll,
  checkIfAllSelect
}: any) => {
  const queryClient = useQueryClient();

  const [users, setUsers] = useState<any>([]);
  // const [loading, setLoading] = useState<any>(true);
  const [createMode, setCreateMode] = useState<boolean>(false);

  const [editMode, setEditMode] = useState<boolean>(false);
  const [addMember, setAddMember] = useState<boolean>(false);
  const [opened, setOpened] = useState<string>("");
  const { data: session }: any = useSession();
  // const [results, setResults] = useState([]);
  const inputRef = useRef<null | HTMLInputElement>(null);
  const [search, setSearch] = useState("");
  const debouncedSearch = useDebounce(search, 500);

  const [addGroupLoading, setAddGroupLoading] = useState<{ id: string; loading: boolean }>({
    id: "",
    loading: true
  });
  const [selectedGroup, setSelectedGroup] = useState<any>({
    name: "",
    description: "",
    image: ""
  });

  const { setShowPaticipantsBar } = usePaticipantsInvite();

  const [allGroupsRef, allCalInView] = useInView({
    threshold: 1.0
  });

  const {
    data: guestsData,
    fetchNextPage: fetchNextPageCal,
    isLoading: loading,
    isRefetching,
    isFetchingNextPage,
    isFetching,
    refetch
  } = useGuestlistGroup(debouncedSearch);

  useEffect(() => {
    if (allCalInView && !isFetchingNextPage && !loading) fetchNextPageCal();
  }, [allCalInView, fetchNextPageCal, isFetchingNextPage, loading]);

  const handleSearch = (e: any) => {
    setSearch(e.target.value);
  };

  const handleCreate = () => {
    setCreateMode(true);
    setShowPaticipantsBar(false);
    setSelectedGroup({});
  };

  const handleEdit = (group: any) => {
    setEditMode(true);
    setSelectedGroup(group);
    // setAddMember(true);
    setShowPaticipantsBar(false);
  };

  const handleAddGuest = async (id: string, toggle?: boolean, bulkAction = false) => {
    // setLoading(true);
    if (!toggle) {
      if (bulkAction) {
        setUsers((prevState: any) =>
          prevState.map((state: any) => ({ ...state, selected: false }))
        );
        setEmails([]);
      } else {
        setUsers((prevState: any) =>
          prevState.map((state: any) => ({
            ...state,
            selected: state.groupId === id ? false : state.selected
          }))
        );
        setEmails((prevState: any) => prevState.filter((email: any) => email.groupId !== id));
        setAddGroupLoading({ id: "", loading: false });
      }
    } else {
    }
  };

  const handleSelectAll = async (e: boolean) => {
    if (e) {
      const _data = users.map((d: any) => ({ ...d, selected: true }));
      setUsers(_data);
      setEmails((prevState: any) => {
        const filteredPrevState = prevState.filter((email: any) => email.groupId !== opened);
        // Add new emails
        return [
          ...filteredPrevState,
          ..._data.map((d: any) => ({
            ...d,
            selected: true,
            groupId: opened
          }))
        ];
      });
    } else {
      setUsers((prevState: any) => prevState.map((state: any) => ({ ...state, selected: false })));
      setEmails((prevState: any) => prevState.filter((email: any) => email.groupId !== opened));
    }
  };

  const handleFetchGroupGuest = async (id: string, select?: boolean) => {
    setOpened(id);
  };

  const handleToggleEmailSelection = (user: any) => {
    setEmails((prevEmails: any) => {
      const isAlreadySelected = prevEmails.some((e: any) => e.email === user.email);
      if (isAlreadySelected) {
        return prevEmails.filter((e: any) => e.email !== user.email);
      } else {
        return [...prevEmails, { email: user?.email, groupId: opened, role, selected: true }];
      }
    });
  };

  const slideAnimation = {
    hidden: { x: "100%" }, //
    visible: { x: 0 },
    exit: { x: "100%" }
  };

  const countUsersWithGroupId = emails.filter(
    ({ groupId }: { groupId: string }) => groupId !== undefined
  ).length;

  return (
    <div className="h-full overflow-y-auto">
      {createMode || editMode ? (
        <motion.div
          className="bg-white py-l z-20 h-full w-full absolute left-0 right-0 bottom-0 top-0"
          variants={slideAnimation}
          initial="hidden"
          animate="visible"
          exit="exit"
          transition={{ type: "tween", duration: 0.05 }}
        >
          <CreateGroup
            mode={editMode ? "edit" : "create"}
            setMode={editMode ? setEditMode : setCreateMode}
            selectedGroup={selectedGroup}
            setShowParticipantsBar={setShowPaticipantsBar}
            setAddMember={setAddMember}
            addMember={addMember}
            refetch={refetch}
            setSelectedGroup={setSelectedGroup}
            setOpened={setOpened}
          />
          {editMode && addMember ? (
            <motion.div
              className="bg-white p-ml z-20 h-full w-full absolute left-0 right-0 bottom-0 top-0"
              variants={slideAnimation}
              initial="hidden"
              animate="visible"
              exit="exit"
              transition={{ type: "tween", duration: 0.05 }}
            >
              <AddMember
                selectedGroup={selectedGroup}
                setAddMember={setAddMember}
                setShowPaticipantsBar={setShowPaticipantsBar}
                addMember={addMember}
                refetch={refetch}
              />
            </motion.div>
          ) : null}
        </motion.div>
      ) : null}

      {opened && !editMode ? (
        <motion.div
          className="bg-white p-ml z-20 h-full w-full absolute left-0 right-0 bottom-0 top-0 overflow-y-auto"
          variants={slideAnimation}
          initial="hidden"
          animate="visible"
          exit="exit"
          transition={{ type: "tween", duration: 0.05 }}
        >
          <GroupGuests
            selectedGroup={selectedGroup}
            setOpened={setOpened}
            users={users}
            handleToggleEmailSelection={handleToggleEmailSelection}
            emails={emails}
            handleSelectAll={handleSelectAll}
            toggleUserSelection={toggleUserSelection}
            checkIsUserPresent={checkIsUserPresent}
            toggleSelectAll={toggleSelectAll}
            checkIfAllSelect={checkIfAllSelect}
            // setEditMode={setEditMode}
            onEditGroupClick={() => {
              setEditMode(true);
            }}
          />
        </motion.div>
      ) : null}

      <div className="relative h-full overflow-y-auto ">
        <div className="h-full">
          {!createMode && !addMember ? (
            <Fragment>
              <div className="h-full overflow-y-auto">
                <div className="w-full">
                  <div className="py-m px-l   flex items-center justify-between mt-[2px] gap-2">
                    <div className=" w-full">
                      <Input
                        // type="search"
                        ref={inputRef}
                        variant="medium-s"
                        warpperClassName=" "
                        className={`!bg-[#F5F6F9] px-2  `}
                        inputClassName={`w-full  !bg-[#F5F6F9] px-2`}
                        placeholder="Search"
                        value={search}
                        onChange={handleSearch}
                        spellCheck="false"
                        rightSectionClassName="bg-transparent mx-2 py-1 items-center content-center p-none shadow-none m-auto"
                        rightSection={
                          search.length > 0 && (
                            <X
                              size="20"
                              className="text-color-secondary cursor-pointer"
                              onClick={() => {
                                setSearch("");
                              }}
                            />
                          )
                        }
                        leftSectionBgClassName="bg-transparent mx-2 py-1 items-center content-center p-none shadow-none m-auto"
                        leftSection={
                          <SearchNormal1
                            size="20"
                            className={`cursor-pointer  p-0`}
                            // variant="Outline"
                          />
                        }
                      />
                    </div>
                    <div className="flex items-center gap-s mr-xs !bg-[#F5F6F9] p-3 rounded-8">
                      <Text
                        variant={"body_two_strong"}
                        className="cursor-pointer flex items-center gap-xxs"
                        onClick={handleCreate}
                      >
                        <Add /> Group
                      </Text>
                    </div>
                  </div>
                </div>
                <div className="h-full overflow-y-auto">
                  <div className="h-[calc(100dvh-200px)] overflow-y-auto mt-l pb-20">
                    {loading ? (
                      <SkeletonListLoader />
                    ) : (
                      <>
                        {guestsData &&
                        guestsData.pages &&
                        guestsData.pages.length > 0 &&
                        guestsData.pages[0]?.data?.length > 0 ? (
                          guestsData.pages.map((page: any) =>
                            page?.data?.map((group: any, index: number) => (
                              <React.Fragment key={index}>
                                <Fragment>
                                  <div
                                    className="cursor-pointer"
                                    onClick={(e) => {
                                      setSelectedGroup(group);
                                      handleFetchGroupGuest(group.id);
                                    }}
                                    key={JSON.stringify(group)}
                                  >
                                    <Multiline
                                      className="bg-transparent px-0 !mx-0"
                                      icon={
                                        <div
                                          className="relative flex gap-m items-center cursor-pointer"
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            if (group?.numberOfGuests === 0) return;
                                            toggleSelectAll(
                                              "Group",
                                              group?.id,
                                              [],
                                              checkIfAllSelect("Group", group?.id) ? false : true,
                                              group?.numberOfGuests
                                            );
                                          }}
                                        >
                                          <Fragment>
                                            <Avatar
                                              imgSrc={group?.image}
                                              size={56}
                                              activeRing={false}
                                              variant="image"
                                              present={false}
                                              presence={false}
                                              presenceState="blocked"
                                            />
                                            {addGroupLoading.loading &&
                                            addGroupLoading.id === group.id ? (
                                              <div className="absolute top-0 right-0 left-0 bottom-0 flex justify-center items-center bg-black opacity-75 rounded-8 p-1">
                                                <Loader size="small" />
                                              </div>
                                            ) : (
                                              checkIfAllSelect("Group", group?.id) && (
                                                <div className="absolute top-0 right-0 left-0 bottom-0 flex justify-center items-center bg-black opacity-75 rounded-8 p-1">
                                                  <Check size={16} className="text-white" />
                                                </div>
                                              )
                                            )}
                                          </Fragment>
                                        </div>
                                      }
                                      titleText={group?.name}
                                      titleTextClassName="break-all"
                                      text1={`${
                                        checkIfAllSelect("Group", group?.id)
                                          ? `${group?.numberOfGuests ?? 0}/${group?.numberOfGuests ?? 0}`
                                          : resourceState?.Group?.[group?.id]?.selected?.length > 0
                                            ? `${resourceState?.Group?.[group?.id]?.selected?.length}/${group?.numberOfGuests ?? 0}`
                                            : `${group?.numberOfGuests ?? 0}`
                                      } People`}
                                      titleTextSize={"body_one_strong"}
                                      text1Size={"caption_one"}
                                      action={
                                        <div className="flex justify-center items-center cursor-pointer gap-m">
                                          {/* <Edit
                                            onClick={(e) => {
                                              e.stopPropagation();
                                              handleEdit(group);
                                            }}
                                            size={14}
                                          /> */}
                                          <ChevronRight size={24} />
                                        </div>
                                      }
                                    />
                                  </div>

                                  <div className="border-b border-gray-200 w-full"></div>
                                </Fragment>
                              </React.Fragment>
                            ))
                          )
                        ) : (
                          <div className="content-center h-full mx-auto text-center">
                            <NoDataView title={`No groups found.`} imageKey={"guest"} />
                          </div>
                        )}
                        {isFetchingNextPage && <SkeletonListLoader />}
                        <div ref={allGroupsRef} className="h-5 " />
                      </>
                    )}
                  </div>
                </div>
              </div>
            </Fragment>
          ) : null}
        </div>
      </div>
    </div>
  );
};

export default Group;
